/**
 * Inicio — dueño con plan original sin soft limit de volumen.
 * Agradecimiento + opción de pasarse al plan Crece (no compite con SoftLimitCreceCard).
 */
import React, { useEffect, useRef, useState } from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { creceLegacyHomeCopy } from '../../../../../../shared/plan-crece-copy';
import { CRECE_FUNNEL_EVENTS } from '../../../../../../shared/crece-funnel';
import { usePlanEntitlements } from '../../providers/PlanEntitlementsProvider';
import { navigateInShell } from '../../navigation/rootNavigation';
import { isBillingEnabled } from '../isBillingEnabled';
import { spacing } from '../../../shared/theme/spacing';
import { ticketColors, ticketFonts } from '../../../shared/theme/ticketColors';
import { trackCreceFunnel } from '../trackCreceFunnel';
import { shouldShowLegacyCreceHomeNudge } from '../legacyCreceHomeNudge';
import {
  dismissSoftLimit,
  isSoftLimitDismissed,
  LEGACY_HOME_DISMISS_PERIOD,
} from '../softLimitDismissal';

const SOURCE = 'legacy_home_nudge';
const KIND = 'legacy_home' as const;

export function LegacyCreceHomeCard() {
  const navigation = useNavigation<any>();
  const { entitlements } = usePlanEntitlements();
  const [dismissed, setDismissed] = useState<boolean | null>(null);
  const viewTracked = useRef(false);

  const visible = shouldShowLegacyCreceHomeNudge({
    billingEnabled: isBillingEnabled(),
    isOwner: entitlements?.is_owner,
    planCode: entitlements?.plan_code,
    volumeNear: entitlements?.volume_soft_limit?.near === true,
  });

  const orgId = entitlements?.organization_id ?? '';

  useEffect(() => {
    let cancelled = false;
    if (!visible || !orgId) {
      setDismissed(null);
      viewTracked.current = false;
      return;
    }
    setDismissed(null);
    viewTracked.current = false;
    void isSoftLimitDismissed(orgId, LEGACY_HOME_DISMISS_PERIOD, KIND)
      .then((v) => {
        if (!cancelled) setDismissed(v);
      })
      .catch(() => {
        if (!cancelled) setDismissed(false);
      });
    return () => {
      cancelled = true;
    };
  }, [visible, orgId]);

  useEffect(() => {
    if (!visible || dismissed !== false || viewTracked.current) return;
    viewTracked.current = true;
    trackCreceFunnel(CRECE_FUNNEL_EVENTS.softLimitView, { source: SOURCE });
  }, [visible, dismissed]);

  if (!visible || !orgId || dismissed !== false) return null;

  return (
    <View style={styles.card} testID="home.legacy-crece-nudge">
      <Text style={styles.eyebrow}>{creceLegacyHomeCopy.eyebrow}</Text>
      <Text style={styles.title}>{creceLegacyHomeCopy.title}</Text>
      <Text style={styles.body}>{creceLegacyHomeCopy.body}</Text>
      <TouchableOpacity
        style={styles.primaryBtn}
        onPress={() => {
          trackCreceFunnel(CRECE_FUNNEL_EVENTS.ctaClick, {
            source: SOURCE,
            plan_code: 'crece',
          });
          navigateInShell(navigation, 'Billing');
        }}
        testID="home.legacy-crece-nudge.cta"
      >
        <Text style={styles.primaryBtnText}>{creceLegacyHomeCopy.cta}</Text>
      </TouchableOpacity>
      <TouchableOpacity
        onPress={() => {
          trackCreceFunnel(CRECE_FUNNEL_EVENTS.softLimitDismiss, { source: SOURCE });
          void dismissSoftLimit(orgId, LEGACY_HOME_DISMISS_PERIOD, KIND);
          setDismissed(true);
        }}
        testID="home.legacy-crece-nudge.dismiss"
      >
        <Text style={styles.dismiss}>{creceLegacyHomeCopy.dismiss}</Text>
      </TouchableOpacity>
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    borderRadius: 14,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.brandSoft,
    padding: spacing.md,
    gap: spacing.xs,
    marginBottom: spacing.md,
  },
  eyebrow: {
    fontFamily: ticketFonts.sans,
    fontSize: 11,
    fontWeight: '700',
    color: ticketColors.ink3,
    textTransform: 'uppercase',
  },
  title: {
    fontFamily: ticketFonts.sans,
    fontWeight: '800',
    fontSize: 16,
    color: ticketColors.ink,
  },
  body: {
    fontFamily: ticketFonts.sans,
    color: ticketColors.ink2,
    lineHeight: 20,
  },
  primaryBtn: {
    marginTop: spacing.xs,
    borderRadius: 10,
    backgroundColor: ticketColors.crece,
    paddingVertical: spacing.sm,
    alignItems: 'center',
  },
  primaryBtnText: {
    fontFamily: ticketFonts.sans,
    color: ticketColors.creceInk,
    fontWeight: '800',
  },
  dismiss: {
    fontFamily: ticketFonts.sans,
    color: ticketColors.ink2,
    textAlign: 'center',
    marginTop: spacing.xs,
    fontWeight: '600',
  },
});
